// components/data-quality/QualityTrendChart.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const QualityTrendChart = ({ trendData }) => {
  const [showDetails, setShowDetails] = useState(false);

  // Format months for better display (e.g., "2025-04" to "Apr 2025")
  const formatMonth = (monthStr) => {
    try {
      const [year, month] = monthStr.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return monthStr;
    }
  };

  const chartData = {
    labels: trendData.map((item) => formatMonth(item.month)),
    datasets: [
      {
        label: "Average Quality Score",
        data: trendData.map((item) => item.averageQualityScore),
        borderColor: "rgba(59, 130, 246, 1)",
        backgroundColor: "rgba(59, 130, 246, 0.5)",
        yAxisID: "y",
      },
      ...(showDetails
        ? [
            {
              label: "Abandoned Rate (%)",
              data: trendData.map((item) => item.abandonedPercentage),
              borderColor: "rgba(239, 68, 68, 1)",
              backgroundColor: "rgba(239, 68, 68, 0.5)",
              yAxisID: "y1",
              borderDash: [5, 5],
            },
            {
              label: "Perfect Quality Rate (%)",
              data: trendData.map((item) => item.perfectQualityPercentage),
              borderColor: "rgba(34, 197, 94, 1)",
              backgroundColor: "rgba(34, 197, 94, 0.5)",
              yAxisID: "y1",
              borderDash: [5, 5],
            },
          ]
        : []),
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Average Quality Score",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      ...(showDetails
        ? {
            y1: {
              type: "linear",
              display: true,
              position: "right",
              title: {
                display: true,
                text: "Percentage",
                font: {
                  size: 12,
                  weight: "bold",
                },
              },
              min: 0,
              max: 100,
              grid: {
                drawOnChartArea: false,
              },
              ticks: {
                callback: (value) => `${value}%`,
              },
            },
          }
        : {}),
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += `${context.parsed.y.toFixed(1)}%`;
            }
            return label;
          },
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartLine} className="text-blue-600 mr-2" />
          Quality Score Trend
        </h3>
        <button
          className="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center"
          onClick={() => setShowDetails(!showDetails)}
        >
          <FontAwesomeIcon
            icon={showDetails ? faToggleOn : faToggleOff}
            className="mr-1"
          />
          {showDetails ? "Hide Details" : "Show Details"}
        </button>
      </div>
      <div className="flex-grow">
        <Line data={chartData} options={chartOptions} />
      </div>
      <div className="mt-4 text-xs text-center text-gray-500">
        {trendData.length === 0
          ? "No trend data available"
          : "Trend showing quality score evolution over time"}
      </div>
    </div>
  );
};

export default QualityTrendChart;
